import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ChevronLeft,
  ChevronRight,
  GalleryHorizontalEnd,
  X
} from 'lucide-react';
import mergeRefs from 'merge-refs';
import {
  animate,
  AnimatePresence,
  motion,
  type MotionValue,
  motionValue,
  useReducedMotion
} from 'motion/react';
import {
  type ComponentPropsWithoutRef,
  createContext,
  type Dispatch,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
  type PointerEvent,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { keepAnimationInJs } from 'v5/platform/react/keepAnimationInJs';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import { usePortalContainer } from 'v5/primitives/PortalContainer';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { Button } from './Button';
import {
  ImagePreview,
  type ImagePreviewImageProps,
  type ImagePreviewProps
} from './ImagePreview';
import { Kbd } from './Kbd';
import { MediaBackdrop } from './MediaBackdrop';
import {
  MediaLayer,
  useMediaLayerHold,
  useMediaLayerStack
} from './MediaLayers';

/** What the parts inside bound to a key, by `KeyboardEvent.key`. */
const KeyHandlersContext = createContext<MutableRefObject<
  Map<string, () => void>
> | null>(null);

/**
 * While the calling part is mounted, `key` anywhere in the dialog runs
 * `handler` (a letter in lower case), except where typing or a list owns it.
 */
function useDialogKey(key: string, handler: () => void) {
  const handlers = useContext(KeyHandlersContext);
  const latest = useRef(handler);
  useLayoutEffect(() => {
    latest.current = handler;
  });
  useEffect(() => {
    if (!handlers) return;
    const run = () => latest.current();
    handlers.current.set(key, run);
    return () => {
      if (handlers.current.get(key) === run) handlers.current.delete(key);
    };
  }, [handlers, key]);
}

/** Keys there move a caret, a slider or a list, or type, not the viewer. */
function ownsKeys(target: EventTarget) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      target.closest(
        'input, textarea, select, [role="slider"], [role="listbox"], [role="menu"], [role="tablist"], [role="radiogroup"], [role="grid"]'
      ) !== null)
  );
}

/**
 * `true` once the mouse has rested for `ms`; never where there is no mouse to
 * wake it again. `wake` restarts the wait.
 */
function useIdleAfter(ms: number, active: boolean) {
  const [idle, setIdle] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const wake = useCallback(() => {
    setIdle(false);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setIdle(true), ms);
  }, [ms]);
  useEffect(() => {
    if (active && window.matchMedia('(hover: hover)').matches) wake();
    return () => window.clearTimeout(timer.current);
  }, [active, wake]);
  return { idle, wake };
}

/** Where the `MediaDisplay` is in the dialog, so a scene can draw into it. */
type StageRect = { left: number; top: number; width: number; height: number };

/** What a finger's swipe across the stage does. */
type StageSwipe = { onPrevious(): void; onNext(): void };

const StageContext = createContext<{
  rect: StageRect | null;
  report(rect: StageRect | null): void;
  /** Offered by `MediaDisplay.Navigation` while it is shown. */
  swipe: StageSwipe | null;
  offerSwipe: Dispatch<SetStateAction<StageSwipe | null>>;
} | null>(null);

export interface MediaDialogProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  open: boolean;
  onOpenChange(open: boolean): void;
}

/**
 * A full-size viewer for one file of a collection, over a dark, blurred
 * backdrop in either theme. Compose it from a `MediaDialog.Scene` (what is
 * shown), a `MediaColumnLayout` (a `MediaDisplay` over `MediaThumbnails` or a
 * `MediaToolbar`) and, beside it, a `MediaSidebar`; put the close button where
 * it reads (`MediaDisplay.Close`, `MediaSidebar.Close`) and a
 * `MediaDialog.Title` (or `MediaToolbar.Name`) somewhere inside. The round
 * controls over the media fade away after three seconds without a mouse
 * move.
 */
const MediaDialogRoot = forwardRef<HTMLDivElement, MediaDialogProps>(
  (
    {
      open,
      onOpenChange,
      className,
      children,
      onKeyDown,
      onPointerMove,
      onPointerDownCapture,
      onOpenAutoFocus,
      onCloseAutoFocus,
      ...props
    },
    ref
  ) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const opener = useRef<HTMLElement | null>(null);
    // Read as the dialog opens, before its content mounts: a field in it that
    // takes focus as it mounts (`autoFocus`) would hide what opened it.
    const wasOpen = useRef(false);
    if (open !== wasOpen.current) {
      wasOpen.current = open;
      if (open && document.activeElement instanceof HTMLElement) {
        opener.current = document.activeElement;
      }
    }
    const portalContainer = usePortalContainer();
    const keyHandlers = useRef(new Map<string, () => void>());
    const { idle, wake } = useIdleAfter(3000, open);
    const [stage, setStage] = useState<StageRect | null>(null);
    const [swipe, setSwipe] = useState<StageSwipe | null>(null);
    const runKey = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      const handler = keyHandlers.current.get(
        event.key.length === 1 ? event.key.toLowerCase() : event.key
      );
      if (
        !handler ||
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        ownsKeys(event.target)
      ) {
        return;
      }
      event.preventDefault();
      handler();
    };

    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        {/* Presence is ours, not Radix's: the content keeps its last
            children while it animates out. */}
        <AnimatePresence>
          {open && (
            <DialogPrimitive.Portal forceMount container={portalContainer}>
              <DialogPrimitive.Overlay forceMount asChild>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/90"
                  initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                  animate={{ opacity: 1, backdropFilter: 'blur(12px)' }}
                  exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </DialogPrimitive.Overlay>
              <StopPropagation>
                <DialogPrimitive.Content
                  forceMount
                  asChild
                  aria-describedby={undefined}
                  data-idle={idle || undefined}
                  onKeyDown={runKey}
                  // The dialog itself takes focus, so its keys work at once
                  // and no button shows a ring before Tab.
                  onOpenAutoFocus={event => {
                    onOpenAutoFocus?.(event);
                    if (event.defaultPrevented) return;
                    event.preventDefault();
                    const dialog = dialogRef.current;
                    // A field that took focus as it mounted keeps it.
                    if (
                      dialog !== null &&
                      document.activeElement !== dialog &&
                      dialog.contains(document.activeElement)
                    ) {
                      return;
                    }
                    dialog?.focus();
                  }}
                  // No Radix trigger: back to what opened it, if still there.
                  onCloseAutoFocus={event => {
                    onCloseAutoFocus?.(event);
                    if (event.defaultPrevented) return;
                    event.preventDefault();
                    if (opener.current?.isConnected) opener.current.focus();
                  }}
                  onPointerMove={event => {
                    onPointerMove?.(event);
                    if (event.pointerType === 'mouse') wake();
                  }}
                  // A tablet with a trackpad: the mouse hid them, a finger
                  // brings them back.
                  onPointerDownCapture={event => {
                    onPointerDownCapture?.(event);
                    if (idle) wake();
                  }}
                  {...props}
                >
                  <motion.div
                    ref={mergeRefs(dialogRef, ref)}
                    data-media-dialog
                    className={cn(
                      'group/media fixed inset-0 z-50 m-auto flex h-[min(90vh,64rem)] w-[min(94vw,96rem)] flex-col overflow-hidden bg-charcoal-dark shadow-2xl outline-none sm:rounded-3xl lg:flex-row landscape:flex-row',
                      className
                    )}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <KeyHandlersContext.Provider value={keyHandlers}>
                      <StageContext.Provider
                        value={{
                          rect: stage,
                          report: setStage,
                          swipe,
                          offerSwipe: setSwipe
                        }}
                      >
                        {children}
                      </StageContext.Provider>
                    </KeyHandlersContext.Provider>
                  </motion.div>
                </DialogPrimitive.Content>
              </StopPropagation>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>
    );
  }
);
MediaDialogRoot.displayName = 'MediaDialog';

/** The dialog's accessible name, unstyled, for a title placed in a sidebar. */
const MediaDialogTitle = DialogPrimitive.Title;

type SwipeTrack = {
  pointerId: number;
  /** The file the finger came down on; another one ends the drag. */
  subject: string;
  offset: MotionValue<number>;
  startX: number;
  startY: number;
  dragging: boolean;
  /** The last 100 ms of the drag, for its speed at release. */
  samples: { time: number; x: number }[];
};

/**
 * Where a navigation offers a swipe, the scene follows a finger or a pen
 * (never a mouse) dragged sideways: the current picture moves with it, with
 * growing resistance, and on release a drag past 15 % of the stage or a
 * flick goes to the next or previous file; anything less springs back. A
 * tap stays a tap, a vertical drag and a pinch stay the browser's.
 */
function useSwipeSurface(
  subject: string,
  offset: MotionValue<number> | undefined
): ComponentPropsWithoutRef<'div'> {
  const stage = useContext(StageContext);
  const swipe = stage?.swipe;
  const width = stage?.rect?.width ?? 0;
  const reduceMotion = useReducedMotion();
  const track = useRef<SwipeTrack | null>(null);
  const swallowClick = useRef(false);
  const settle = (value: MotionValue<number>) => {
    if (reduceMotion) value.jump(0);
    else animate(value, 0, { type: 'spring', stiffness: 500, damping: 40 });
  };
  const latestSettle = useRef(settle);
  useLayoutEffect(() => {
    latestSettle.current = settle;
  });
  // The file changed under the finger (a key, the host): that drag is over.
  useEffect(() => {
    const current = track.current;
    if (current && current.subject !== subject) {
      track.current = null;
      latestSettle.current(current.offset);
    }
  }, [subject]);
  if (!swipe || !offset) return {};

  const abort = () => {
    const current = track.current;
    track.current = null;
    if (current?.dragging) settle(current.offset);
  };
  return {
    className: 'touch-pan-y touch-pinch-zoom',
    onPointerDown(event: PointerEvent<HTMLDivElement>) {
      swallowClick.current = false;
      if (event.pointerType === 'mouse') return;
      // A second finger: a pinch, not a swipe.
      if (track.current) return abort();
      if (!event.isPrimary) return;
      track.current = {
        pointerId: event.pointerId,
        subject,
        offset,
        startX: event.clientX,
        startY: event.clientY,
        dragging: false,
        samples: [{ time: event.timeStamp, x: event.clientX }]
      };
    },
    onPointerMove(event: PointerEvent<HTMLDivElement>) {
      const current = track.current;
      if (current?.pointerId !== event.pointerId) return;
      const dx = event.clientX - current.startX;
      const dy = event.clientY - current.startY;
      if (!current.dragging) {
        if (Math.hypot(dx, dy) < 10) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          track.current = null;
          return;
        }
        current.dragging = true;
        current.offset.stop();
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // A pointer the browser doesn't track (a test's) can't be captured;
          // its events come here anyway.
        }
      }
      current.samples = [
        ...current.samples.filter(
          sample => event.timeStamp - sample.time < 100
        ),
        { time: event.timeStamp, x: event.clientX }
      ];
      const limit = (width || event.currentTarget.offsetWidth) / 2;
      current.offset.set(
        Math.sign(dx) * limit * (1 - 1 / (1 + Math.abs(dx) / limit))
      );
    },
    onPointerUp(event: PointerEvent<HTMLDivElement>) {
      const current = track.current;
      if (current?.pointerId !== event.pointerId) return;
      track.current = null;
      if (!current.dragging) return;
      swallowClick.current = true;
      const dx = event.clientX - current.startX;
      const [first] = current.samples.filter(
        sample => event.timeStamp - sample.time < 100
      );
      const elapsed = first ? event.timeStamp - first.time : 0;
      // px/ms, positive along the drag.
      const speed =
        elapsed > 0 ? ((event.clientX - first.x) / elapsed) * Math.sign(dx) : 0;
      const far =
        Math.abs(dx) >= (width || event.currentTarget.offsetWidth) * 0.15;
      // Far, unless thrown back; or a flick, longer than a sloppy tap.
      if ((far && speed > -0.3) || (speed > 0.4 && Math.abs(dx) >= 24)) {
        if (dx < 0) swipe.onNext();
        else swipe.onPrevious();
      }
      settle(current.offset);
    },
    // The browser took over (a pinch, a scroll): never a swipe.
    onPointerCancel(event: PointerEvent<HTMLDivElement>) {
      if (track.current?.pointerId === event.pointerId) abort();
    },
    onClickCapture(event: MouseEvent<HTMLDivElement>) {
      if (!swallowClick.current) return;
      swallowClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  };
}

/**
 * What the dialog shows for one `subject` (the file's key): the picture,
 * enlarged and blurred, over the whole dialog (around a sidebar and under the
 * thumbnails too), with `children` (a `MediaDisplay.Picture`, a document's
 * icon) laid over it where the `MediaDisplay` is. The two are one layer of
 * `MediaLayers`: a new subject's fades in whole over the ones before, which
 * stay as they are until it covers them, so a portrait giving way to a
 * landscape never shows the dialog through, however fast the files change. A
 * layer waits for its picture; while it waits, the one shown dims, and an
 * aurora follows under it. Without a `backdrop` (a document) the layer is
 * just dark.
 */
function MediaDialogScene({
  subject,
  backdrop,
  children
}: {
  subject: string;
  backdrop?: string | null;
  children: ReactNode;
}) {
  const { t } = useTranslation('ds', { keyPrefix: 'MediaDialog' });
  const stage = useContext(StageContext)?.rect;
  const stack = useMediaLayerStack({
    subject,
    under: backdrop ? <MediaBackdrop src={backdrop} /> : null,
    content: children,
    // how far a swipe has carried the layer's picture sideways
    createData: () => motionValue(0)
  });
  const swipeSurface = useSwipeSurface(
    subject,
    stack.top.subject === subject ? stack.top.data : undefined
  );
  return (
    <div
      {...swipeSurface}
      className={cn('absolute inset-0 overflow-hidden', swipeSurface.className)}
    >
      {stack.layers.map(layer => (
        <MediaLayer
          key={layer.id}
          // the picture waits for the stage it is sized to
          layer={stage ? layer : { ...layer, content: null }}
          onReady={() => stack.onReady(layer.id)}
          onSettled={() => stack.onSettled(layer.id)}
          className="dark bg-bg-default"
          auroraClassName="mix-blend-screen text-white [--fx-intensity:.1]"
          contentClassName="absolute flex items-center justify-center"
          contentStyle={stage ? { ...stage, x: layer.data } : undefined}
        />
      ))}
      {stack.loading && (
        // The aurora under the picture shows the wait; this names it.
        // A status takes no name from its text; the text is what is read.
        <span role="status" aria-label={t('loading')} className="sr-only">
          {t('loading')}
        </span>
      )}
    </div>
  );
}

/**
 * The media and what sits under it, stacked; fills what a sidebar leaves.
 * Clicks pass through to the scene below, but for its controls.
 */
export const MediaColumnLayout = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'dark pointer-events-none relative flex min-h-0 min-w-0 flex-1 flex-col text-fg-default',
      className
    )}
    {...props}
  />
));
MediaColumnLayout.displayName = 'MediaColumnLayout';

/**
 * A round, see-through control over the dark backdrop; fades away while the
 * mouse rests, unless it has keyboard focus.
 */
const ViewerButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'pointer-events-auto flex size-14 items-center justify-center rounded-full bg-white/10 text-white outline-none backdrop-blur-md transition-[opacity,background-color] duration-300',
      'hocus:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/70',
      'disabled:cursor-default disabled:opacity-50 disabled:hocus:bg-white/10',
      // Idle hides them, but not the one with focus or under the pointer; a
      // finger can't hit one unseen (a mouse wakes them on its way there).
      'group-data-idle/media:[&:not(:focus,:hover)]:opacity-0 pointer-coarse:group-data-idle/media:[&:not(:focus,:hover)]:pointer-events-none',
      className
    )}
    {...props}
  />
));
ViewerButton.displayName = 'ViewerButton';

/**
 * Where the media is shown, in the space the layout gives it: the scene draws
 * the picture there, under the controls this holds (navigation, close).
 */
const MediaDisplayRoot = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => {
  const ownRef = useRef<HTMLDivElement>(null);
  const report = useContext(StageContext)?.report;
  useLayoutEffect(() => {
    const element = ownRef.current;
    const dialog = element?.closest<HTMLElement>('[data-media-dialog]');
    if (!element || !dialog || !report) return;
    // Offsets, not client rects: those would include the dialog's zoom-in.
    const measure = () => {
      let left = 0;
      let top = 0;
      for (
        let at: Element | null = element;
        at instanceof HTMLElement && at !== dialog;
        at = at.offsetParent
      ) {
        left += at.offsetLeft;
        top += at.offsetTop;
      }
      report({
        left,
        top,
        width: element.offsetWidth,
        height: element.offsetHeight
      });
    };
    measure();
    // A frame later: the scene moving in step would resize what is observed
    // within the same notification.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(element);
    observer.observe(dialog);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      report(null);
    };
  }, [report]);
  return (
    <div
      ref={mergeRefs(ownRef, ref)}
      className={cn('relative min-h-0 flex-1', className)}
      {...props}
    />
  );
});
MediaDisplayRoot.displayName = 'MediaDisplay';

type Size = { w: number; h: number };

/** The picture without a fade of its own: the scene's layer fades in whole. */
function UnfadedImage({
  initial,
  animate,
  transition,
  onUpdate,
  ...props
}: ImagePreviewImageProps) {
  return <img {...props} />;
}

export type MediaDisplayPictureProps = Omit<
  ImagePreviewProps,
  'src' | 'alt' | 'objectFit' | 'ref'
> & { src: string; alt: string };

/**
 * An `ImagePreview` (its loader and error state) as large as the stage
 * allows without cropping, but never past its own size, so a small one
 * stays sharp with the backdrop around it. Sized from the picture's own
 * proportions, so one without stated dimensions (an SVG) shows too.
 */
function MediaDisplayPicture({
  src,
  alt,
  className,
  onLoad,
  onError,
  ImageComponent = UnfadedImage,
  ...props
}: MediaDisplayPictureProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<Size | null>(null);
  const [natural, setNatural] = useState<Size | null>(null);
  const release = useMediaLayerHold();
  useLayoutEffect(() => {
    const element = frameRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setFrame({ w: entry.contentRect.width, h: entry.contentRect.height })
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const scale =
    natural && frame
      ? Math.min(frame.w / natural.w, frame.h / natural.h, 1)
      : null;

  return (
    <div
      ref={frameRef}
      // Clear of the previous / next buttons, as far from them as they are
      // from the edges.
      className="absolute inset-4 flex items-center justify-center sm:inset-x-22"
    >
      {/* Fills the frame while loading or failed, so the loader and the
          fallback do too. */}
      <ImagePreview
        ref={previewRef}
        src={src}
        alt={alt}
        objectFit="fill"
        loading="eager"
        ImageComponent={ImageComponent}
        className={cn(
          'size-full rounded-xl shadow-[0_30px_80px_rgb(0_0_0/0.5)]',
          className
        )}
        style={
          natural && scale !== null
            ? { width: natural.w * scale, height: natural.h * scale }
            : undefined
        }
        onLoad={() => {
          const image =
            previewRef.current?.querySelector<HTMLImageElement>(':scope > img');
          setNatural({
            w: image?.naturalWidth || 1,
            h: image?.naturalHeight || 1
          });
          release();
          onLoad?.();
        }}
        onError={() => {
          setNatural(null);
          release();
          onError?.();
        }}
        {...props}
      />
    </div>
  );
}

/**
 * Previous / next buttons at the stage's sides; the arrow keys do the same
 * anywhere in the dialog, except where they move a caret or a list, and so
 * does a finger's swipe across the stage. `disabled` turns all of them off.
 */
function MediaDisplayNavigation({
  onPrevious,
  onNext,
  disabled = false
}: {
  onPrevious(): void;
  onNext(): void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('ds', { keyPrefix: 'MediaDialog' });
  const noop = () => {};
  useDialogKey('ArrowLeft', disabled ? noop : onPrevious);
  useDialogKey('ArrowRight', disabled ? noop : onNext);
  const offerSwipe = useContext(StageContext)?.offerSwipe;
  const latest = useRef({ onPrevious, onNext });
  useLayoutEffect(() => {
    latest.current = { onPrevious, onNext };
  });
  useEffect(() => {
    if (!offerSwipe || disabled) return;
    const swipe: StageSwipe = {
      onPrevious: () => latest.current.onPrevious(),
      onNext: () => latest.current.onNext()
    };
    offerSwipe(swipe);
    return () => offerSwipe(current => (current === swipe ? null : current));
  }, [offerSwipe, disabled]);
  return (
    <>
      <ViewerButton
        className="absolute left-4 top-1/2 -translate-y-1/2"
        aria-label={t('previous')}
        disabled={disabled}
        onClick={onPrevious}
      >
        <ChevronLeft className="size-5" />
      </ViewerButton>
      <ViewerButton
        className="absolute right-4 top-1/2 -translate-y-1/2"
        aria-label={t('next')}
        disabled={disabled}
        onClick={onNext}
      >
        <ChevronRight className="size-5" />
      </ViewerButton>
    </>
  );
}

/** Closes the dialog from the stage's top-right corner. */
function MediaDisplayClose({
  className,
  disabled
}: {
  className?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('ds', { keyPrefix: 'MediaDialog' });
  return (
    <DialogPrimitive.Close asChild>
      <ViewerButton
        aria-label={t('close')}
        disabled={disabled}
        className={cn('absolute right-4 top-4', className)}
      >
        <X className="size-5" />
      </ViewerButton>
    </DialogPrimitive.Close>
  );
}

/**
 * The collection's files as a strip of thumbnails under the media, on a dark
 * pill over the backdrop; scrolls sideways when they don't fit, and slides
 * open or shut as `shown` changes.
 */
const MediaThumbnailsRoot = forwardRef<
  HTMLDivElement,
  {
    'aria-label': string;
    /** @default true */
    shown?: boolean;
    className?: string;
    children: ReactNode;
  }
>(({ shown = true, className, children, ...props }, ref) => (
  <AnimatePresence initial={false}>
    {shown && (
      <motion.div
        ref={ref}
        role="group"
        aria-label={props['aria-label']}
        data-media-thumbnails=""
        onKeyDown={event => {
          if (event.key !== 'Home' && event.key !== 'End') return;
          const tiles = event.currentTarget.querySelectorAll<HTMLButtonElement>(
            '[data-media-thumbnail]:not(:disabled)'
          );
          const target =
            event.key === 'Home' ? tiles[0] : tiles[tiles.length - 1];
          if (!target) return;
          event.preventDefault();
          target.click();
        }}
        className={cn(
          'pointer-events-auto relative shrink-0 overflow-hidden',
          className
        )}
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        onUpdate={keepAnimationInJs}
      >
        <div className="flex justify-center px-4 pb-4">
          {/* Scrolls like the section tabs: faded edges with a button each
              way, no scrollbar; the pill sits on a parent, as the fade
              would fade the scroller's own background. */}
          <div className="max-w-full overflow-hidden rounded-2xl bg-black/45 backdrop-blur-xl">
            <ScrollShadowWrapper
              orientation="horizontal"
              edge="fade"
              scrollButtons
              className="[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex w-max gap-2 p-2">{children}</div>
            </ScrollShadowWrapper>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
));
MediaThumbnailsRoot.displayName = 'MediaThumbnails';

/**
 * One file in the strip, its picture (or icon) filling the tile. The current
 * one is in full colour, the rest dimmed and greyed until hovered; the
 * current one scrolls into view.
 */
const MediaThumbnailsItem = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'> & { current: boolean }
>(({ current, className, children, ...props }, ref) => {
  const ownRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const node = ownRef.current;
    if (!current || !node) return;
    node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    // Like tabs: when the file changes while the focus is in the strip, the
    // focus goes with it, so the focused tile is always the current one.
    const active = document.activeElement;
    if (
      active !== node &&
      active instanceof HTMLElement &&
      active.closest('[data-media-thumbnails]')?.contains(node)
    ) {
      node.focus({ preventScroll: true });
    }
  }, [current]);
  return (
    <button
      ref={mergeRefs(ownRef, ref)}
      type="button"
      aria-current={current || undefined}
      className={cn(
        'relative flex h-11 w-16 shrink-0 rounded-lg outline-none transition-[filter] duration-200',
        // A finger's hit area reaches into the gaps around the tile.
        'pointer-coarse:after:absolute pointer-coarse:after:-inset-1',
        'focus-visible:ring-2 focus-visible:ring-white/70',
        'disabled:cursor-default disabled:opacity-50',
        !current &&
          'brightness-55 saturate-15 not-disabled:hocus:brightness-80 not-disabled:hocus:saturate-60',
        className
      )}
      {...props}
      data-media-thumbnail=""
      // One stop in the Tab order, the current file; the viewer's arrow keys
      // move on from there. A long strip must not be a dozen stops.
      tabIndex={current ? 0 : -1}
    >
      {/* Clips the picture, not the hit area. */}
      <span className="flex size-full items-center justify-center overflow-hidden rounded-[inherit] bg-white/10 *:size-full *:object-cover">
        {children}
      </span>
    </button>
  );
});
MediaThumbnailsItem.displayName = 'MediaThumbnails.Item';

/** Shows or hides the strip of thumbnails; T does the same anywhere in the dialog. */
function MediaThumbnailsToggle({
  shown,
  onShownChange
}: {
  shown: boolean;
  onShownChange(shown: boolean): void;
}) {
  const { t } = useTranslation('ds', { keyPrefix: 'MediaDialog' });
  useDialogKey('t', () => onShownChange(!shown));
  return (
    <Button
      variant="ghost"
      size="sm"
      // Shows and hides the strip in place (a disclosure): the strip shows
      // the state, so no open-menu look.
      aria-expanded={shown}
      data-expand-target="inline"
      aria-keyshortcuts="T"
      onClick={() => onShownChange(!shown)}
    >
      <GalleryHorizontalEnd />
      {t('thumbnails')}
      {/* `aria-keyshortcuts` says it; the name stays "Thumbnails". */}
      <Kbd aria-hidden className="-mr-1">
        T
      </Kbd>
    </Button>
  );
}

const MediaToolbarRoot = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'dark pointer-events-auto relative flex min-h-14 shrink-0 items-center gap-3 border-t border-fg-default/10 bg-charcoal-dark/90 px-4 py-2 text-sm text-fg-default backdrop-blur-xl',
      className
    )}
    {...props}
  />
));
MediaToolbarRoot.displayName = 'MediaToolbar';

/** The file's name, which also names the dialog. */
const MediaToolbarName = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('min-w-0 truncate text-sm font-medium', className)}
    {...props}
  />
));
MediaToolbarName.displayName = 'MediaToolbar.Name';

/** "3 of 11". */
function MediaToolbarCounter({
  index,
  count,
  className
}: {
  /** Zero-based. */
  index: number;
  count: number;
  className?: string;
}) {
  const { t } = useTranslation('ds', { keyPrefix: 'MediaDialog' });
  return (
    <span
      aria-live="polite"
      aria-atomic
      className={cn('shrink-0 tabular-nums text-fg-default/60', className)}
    >
      {t('counter', { index: index + 1, count })}
    </span>
  );
}

/** What can be done with the file, at the toolbar's far end. */
const MediaToolbarActions = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('ml-auto flex shrink-0 items-center gap-1', className)}
    {...props}
  />
));
MediaToolbarActions.displayName = 'MediaToolbar.Actions';

/**
 * The file's details on the app's own surface: a card floating beside the
 * media (below it on a narrow screen held upright), with the backdrop showing
 * around it; its corners are the dialog's less the gap, so the two curves
 * run parallel.
 * Compose it from `Header`, `Body` (scrolls) and `Footer`.
 */
const MediaSidebarRoot = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<'aside'>
>(({ className, ...props }, ref) => (
  <aside
    ref={ref}
    className={cn(
      'relative m-4 flex shrink-0 flex-col overflow-hidden rounded-lg bg-bg-default text-fg-default shadow-2xl max-lg:portrait:mt-0 max-lg:portrait:max-h-[60%] lg:ml-0 lg:w-116 max-lg:landscape:ml-0 max-lg:landscape:w-[min(29rem,50%)]',
      className
    )}
    {...props}
  />
));
MediaSidebarRoot.displayName = 'MediaSidebar';

const MediaSidebarHeader = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex shrink-0 items-center gap-2 border-b border-border-default px-4 py-3',
      className
    )}
    {...props}
  />
));
MediaSidebarHeader.displayName = 'MediaSidebar.Header';

/** Scrolls between the header and the footer, shadowed where it does. */
const MediaSidebarBody = forwardRef<
  HTMLDivElement,
  { className?: string; children: ReactNode }
>(({ className, children }, ref) => (
  // A column, so content with `min-h-0 flex-1` can fill it and scroll itself
  // (a pinned composer under a thread).
  <ScrollShadowWrapper
    ref={ref}
    edge="soft"
    className={cn('min-h-0 flex-1', className)}
    scrollerClassName="flex flex-col"
  >
    {children}
  </ScrollShadowWrapper>
));
MediaSidebarBody.displayName = 'MediaSidebar.Body';

const MediaSidebarFooter = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('shrink-0 p-4', className)} {...props} />
));
MediaSidebarFooter.displayName = 'MediaSidebar.Footer';

/** Closes the dialog from the sidebar, at the end of its header. */
function MediaSidebarClose({ className }: { className?: string }) {
  const { t } = useTranslation('ds', { keyPrefix: 'MediaDialog' });
  return (
    <DialogPrimitive.Close asChild>
      <Button
        variant="secondary"
        layout="icon"
        size="md"
        shape="rounded"
        aria-label={t('close')}
        className={cn('ml-auto', className)}
      >
        <X />
      </Button>
    </DialogPrimitive.Close>
  );
}

/**
 * Binds a letter anywhere in the dialog to `onPress` while mounted, except
 * where typing or a list owns it; draws nothing. Outside a dialog it does
 * nothing.
 */
function MediaDialogKey({
  keyName,
  onPress
}: {
  /** A letter in lower case. */
  keyName: string;
  onPress(): void;
}) {
  useDialogKey(keyName, onPress);
  return null;
}

export const MediaDialog = Object.assign(MediaDialogRoot, {
  Title: MediaDialogTitle,
  Scene: MediaDialogScene,
  Key: MediaDialogKey
});

export const MediaDisplay = Object.assign(MediaDisplayRoot, {
  Picture: MediaDisplayPicture,
  Navigation: MediaDisplayNavigation,
  Close: MediaDisplayClose
});

export const MediaThumbnails = Object.assign(MediaThumbnailsRoot, {
  Item: MediaThumbnailsItem,
  Toggle: MediaThumbnailsToggle
});

export const MediaToolbar = Object.assign(MediaToolbarRoot, {
  Name: MediaToolbarName,
  Counter: MediaToolbarCounter,
  Actions: MediaToolbarActions
});

export const MediaSidebar = Object.assign(MediaSidebarRoot, {
  Header: MediaSidebarHeader,
  Body: MediaSidebarBody,
  Footer: MediaSidebarFooter,
  Close: MediaSidebarClose
});
